import type { MatchDayDetail, Player } from '../api/types';
import type { DraftMatch } from './matchSchedule';
import type { DraftParticipant } from '../reducers/points';
import { PENALTY_TYPES } from './penalties';
import type { PenaltyType } from './penalties';

export interface LoadedDraft {
  editingId: string;
  dateTime: string;
  comment: string;
  users: DraftParticipant[];
  usersPoints: Player[];
  expandedResults: DraftMatch[];
}

/* A stored Match Day carries ids and names but not Player rows, and the Players who took part
   may since have been deactivated — so the draft is built from the Match Day itself rather
   than from the active-Player list. `short` is not read anywhere in the wizard. */
const playerFrom = (id: string, name: string): Player => ({ _id: id, name, short: '' });

export function draftFrom(matchDay: MatchDayDetail): LoadedDraft {
  const named = new Map<string, string>();
  matchDay.participants.forEach((p) => named.set(p.playerId, p.name));
  matchDay.matches.forEach((m) => {
    named.set(m.player1, m.player1Name);
    named.set(m.player2, m.player2Name);
  });

  const claims = new Map<string, Partial<Record<PenaltyType, boolean>>>();
  matchDay.penalties.forEach((penalty) => {
    if (!PENALTY_TYPES.includes(penalty.penalty as PenaltyType)) {
      return;
    }
    const current = claims.get(penalty.playerId) ?? {};
    current[penalty.penalty as PenaltyType] = true;
    claims.set(penalty.playerId, current);
  });

  /* Participants present are exactly the Players appearing in the Matches: the save route
     checks that the two agree, so deriving one from the other keeps them agreeing. */
  const played = new Set<string>();
  matchDay.matches.forEach((m) => { played.add(m.player1); played.add(m.player2); });

  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'pl');

  return {
    editingId: matchDay._id,
    dateTime: new Date(matchDay.date).toISOString(),
    comment: matchDay.comment ?? '',
    users: matchDay.participants
      .map((p) => ({ ...playerFrom(p.playerId, p.name), penalties: claims.get(p.playerId) ?? {} }))
      .sort(byName),
    usersPoints: [...played]
      .map((id) => playerFrom(id, named.get(id) ?? id))
      .sort(byName),
    expandedResults: matchDay.matches.map((m, index) => ({
      _id: index,
      player1: playerFrom(m.player1, m.player1Name),
      player2: playerFrom(m.player2, m.player2Name),
      player1Score: m.player1Score,
      player2Score: m.player2Score,
      setsNumber: m.setsNumber ?? m.player1Score + m.player2Score
    }))
  };
}
